<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import ExternalAdReplyCard from './ExternalAdReplyCard.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, MESSAGE_VARIANTS, ORIENTATION } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useFunctionGetter } from 'dashboard/composables/store';

const {
  id,
  content,
  attachments,
  contentAttributes,
  messageType,
  inboxId,
  templateParams,
  variant,
  orientation,
} = useMessageContext();
const { t } = useI18n();
const inboxRef = useFunctionGetter('inboxes/getInbox', inboxId);
const showOriginalForDeleted = computed(() => {
  const inbox = inboxRef.value || {};
  // Default false: only marker unless explicitly enabled
  return inbox.show_deleted_message_placeholder === true;
});

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const externalAdReply = computed(() => {
  const attributes = contentAttributes.value || {};
  return attributes.externalAdReply || attributes.external_ad_reply || null;
});

const isDeletedByMarker = computed(() => {
  const text = content.value || '';
  return typeof text === 'string' && text.trim().startsWith('⛔');
});

const renderContent = computed(() => {
  // Base content selection (original vs translation)
  let baseContent = content.value;
  if (renderOriginal.value) {
    baseContent = content.value;
  } else if (hasTranslations.value) {
    baseContent = translationContent.value;
  }

  // If message was deleted and setting disables showing original text,
  // only show the deleted marker (first line)
  const isDeleted = contentAttributes.value?.deleted || isDeletedByMarker.value;
  if (
    isDeleted &&
    !showOriginalForDeleted.value &&
    typeof baseContent === 'string'
  ) {
    return baseContent.split(/\r?\n/)[0];
  }

  return baseContent;
});

// Force re-render when toggle or content state changes
const rerenderKey = ref(0);
watch(
  [
    showOriginalForDeleted,
    () => content.value,
    () => contentAttributes.value?.deleted,
  ],
  () => {
    rerenderKey.value += 1;
  }
);

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  if (externalAdReply.value) {
    return false;
  }

  return !content.value && !attachments.value?.length;
});

const templateHeaderMedia = computed(() => {
  const params = templateParams.value || {};
  const processedParams =
    params.processedParams || params.processed_params || {};
  const header = processedParams.header || {};

  const mediaUrl = header.mediaUrl || header.media_url;
  const mediaTypeRaw = header.mediaType || header.media_type;

  if (!mediaUrl || !mediaTypeRaw) {
    return null;
  }

  const mediaType = String(mediaTypeRaw).toLowerCase();
  const mediaName = header.mediaName || header.media_name || '';

  return { url: mediaUrl, type: mediaType, name: mediaName };
});

const normalizedTemplateParams = computed(() => {
  const params = templateParams.value || {};
  const processedParams =
    params.processedParams || params.processed_params || {};

  const normalizeButtons = buttons => {
    if (!Array.isArray(buttons)) return [];
    return buttons.map(button => ({
      type: button.type || button.sub_type || '',
      parameter: button.parameter || '',
      variables: button.variables || [],
      url: button.url || button.href || button.web_url || button.link || '',
      phoneNumber: button.phone_number || button.phoneNumber || '',
      label: button.text || button.label || '',
    }));
  };

  return {
    name: params.name,
    language: params.language,
    namespace: params.namespace,
    processed: {
      ...processedParams,
      buttons: normalizeButtons(processedParams.buttons),
    },
  };
});

const inboxMessageTemplates = computed(() => {
  const inbox = inboxRef.value || {};
  const directTemplates =
    inbox.message_templates || inbox.messageTemplates || [];
  const additionalAttributes =
    inbox.additional_attributes || inbox.additionalAttributes || {};
  const fallbackTemplates =
    additionalAttributes.message_templates ||
    additionalAttributes.messageTemplates ||
    [];

  const candidates = directTemplates || fallbackTemplates;
  return Array.isArray(candidates) ? candidates : [];
});

const selectedTemplateDefinition = computed(() => {
  const templates = inboxMessageTemplates.value;
  if (!templates.length) return null;

  const { name, language } = normalizedTemplateParams.value;
  if (!name) return null;

  const normalizedName = String(name).toLowerCase();
  const normalizedLanguage = (language || '').toLowerCase();

  const matchByLanguage = templates.find(template => {
    if (!template) return false;
    const templateName = String(template.name || '').toLowerCase();
    if (templateName !== normalizedName) return false;

    if (!normalizedLanguage) return true;
    const templateLanguage = String(template.language || '').toLowerCase();
    return templateLanguage === normalizedLanguage;
  });

  if (matchByLanguage) return matchByLanguage;

  return (
    templates.find(template => {
      const templateName = String(template?.name || '').toLowerCase();
      return templateName === normalizedName;
    }) || null
  );
});

const templateButtonComponents = computed(() => {
  const template = selectedTemplateDefinition.value;
  if (!template || !Array.isArray(template.components)) {
    return [];
  }

  return template.components.filter(
    component => (component.type || '').toUpperCase() === 'BUTTONS'
  );
});

const resolveButtonUrl = (button, processedButton, processedParams) => {
  const rawUrl = button.url || processedButton.url || '';
  if (!rawUrl) return '';

  if (!rawUrl.includes('{{')) {
    return rawUrl;
  }

  const parameter = processedButton.parameter || '';
  if (parameter) {
    return rawUrl.replace(/{{\s*[^}]+\s*}}/g, parameter);
  }

  const bodyParams = processedParams.body || {};
  return rawUrl.replace(/{{\s*([^}]+)\s*}}/g, (_match, variable) => {
    const key = String(variable).trim();
    const numberedValue = bodyParams[key];
    if (numberedValue) return numberedValue;

    const numericKey = Number(key);
    if (!Number.isNaN(numericKey) && bodyParams[numericKey]) {
      return bodyParams[numericKey];
    }

    return `{{${key}}}`;
  });
};

const formatTemplateButtons = () => {
  const buttonGroups = templateButtonComponents.value;
  const processed = normalizedTemplateParams.value.processed || {};
  const processedButtons = processed.buttons || [];

  const renderedButtons = [];

  if (buttonGroups.length) {
    let flatIndex = -1;

    buttonGroups.forEach(component => {
      (component.buttons || []).forEach(button => {
        flatIndex += 1;
        const processedButton = processedButtons[flatIndex] || {};
        const typeRaw = button.type || processedButton.type || '';
        const type = String(typeRaw).toUpperCase();
        const label =
          button.text ||
          button.title ||
          processedButton.label ||
          processedButton.parameter ||
          t('WHATSAPP_TEMPLATES.PARSER.BUTTON_LABEL', {
            index: flatIndex + 1,
          });

        const base = {
          id: `${flatIndex}-${type}-${label}`,
          type,
          label,
          parameter: processedButton.parameter || '',
        };

        if (type === 'URL') {
          base.url = resolveButtonUrl(button, processedButton, processed);
        } else if (type === 'PHONE_NUMBER') {
          base.phoneNumber =
            button.phone_number ||
            button.phoneNumber ||
            processedButton.phoneNumber ||
            '';
        } else if (type === 'COPY_CODE') {
          base.code = processedButton.parameter || '';
        }

        renderedButtons.push(base);
      });
    });
  } else if (processedButtons.length) {
    processedButtons.forEach((button, index) => {
      const type = String(button.type || '').toUpperCase();
      const label =
        button.label ||
        button.parameter ||
        t('WHATSAPP_TEMPLATES.PARSER.BUTTON_LABEL', {
          index: index + 1,
        });

      const base = {
        id: `${index}-${type}-${label}`,
        type,
        label,
        parameter: button.parameter || '',
      };

      if (type === 'URL') {
        base.url = resolveButtonUrl({}, button, processed);
      } else if (type === 'PHONE_NUMBER') {
        base.phoneNumber = button.phoneNumber || '';
      } else if (type === 'COPY_CODE') {
        base.code = button.parameter || '';
      }

      renderedButtons.push(base);
    });
  }

  return renderedButtons;
};

const templateButtons = computed(formatTemplateButtons);

const hasTemplateButtons = computed(() => templateButtons.value.length > 0);

const containerAlignmentClass = computed(() => {
  if (orientation.value === ORIENTATION.RIGHT) return 'items-end';
  if (orientation.value === ORIENTATION.LEFT) return 'items-start';
  return 'items-center';
});

const buttonBubbleAlignmentClass = computed(() => {
  if (orientation.value === ORIENTATION.RIGHT) return 'self-end';
  if (orientation.value === ORIENTATION.LEFT) return 'self-start';
  return 'self-center';
});

const buttonPalette = computed(() => {
  const paletteMap = {
    [MESSAGE_VARIANTS.TEMPLATE]: {
      container: 'text-white shadow-sm',
      button:
        'bg-n-solid-iris/90 border border-white/20 text-white hover:bg-n-solid-iris',
    },
    [MESSAGE_VARIANTS.BOT]: {
      container: 'text-white shadow-sm',
      button:
        'bg-n-solid-iris/90 border border-white/20 text-white dark:text-white hover:bg-n-solid-iris',
    },
    [MESSAGE_VARIANTS.AGENT]: {
      container: 'text-white shadow-sm',
      button:
        'bg-n-solid-blue/90 border border-white/20 text-white dark:text-white hover:bg-n-solid-blue',
    },
    [MESSAGE_VARIANTS.USER]: {
      container: 'bg-n-slate-4 text-white border border-n-slate-5 shadow-sm',
      button:
        'bg-n-slate-4/90 border border-white/20 text-white hover:bg-n-slate-4',
    },
    [MESSAGE_VARIANTS.PRIVATE]: {
      container:
        'bg-n-solid-amber text-n-amber-12 border border-n-solid-amber/50 shadow-sm',
      button:
        'bg-n-solid-amber/70 border border-n-amber-10 text-n-amber-12 hover:bg-n-solid-amber/80',
    },
  };

  return (
    paletteMap[variant.value] || {
      container:
        'bg-n-solid-1 text-n-slate-12 border border-n-weak/60 shadow-sm',
      button:
        'bg-n-slate-6 border border-n-weak/60 text-white hover:bg-n-slate-7',
    }
  );
});

const buttonBubbleSurfaceClass = computed(() => buttonPalette.value.container);
const buttonItemClass = computed(() => buttonPalette.value.button);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <div class="flex flex-col gap-1 max-w-lg" :class="[containerAlignmentClass]">
    <BaseBubble
      :key="`text-${id}-${rerenderKey}`"
      class="px-4 py-3"
      data-bubble-name="text"
    >
      <div class="gap-3 flex flex-col">
        <span v-if="isEmpty" class="text-n-slate-11">
          {{ $t('CONVERSATION.NO_CONTENT') }}
        </span>
        <div
          v-if="templateHeaderMedia"
          class="overflow-hidden rounded-lg border border-n-alpha-4"
        >
          <img
            v-if="templateHeaderMedia.type === 'image'"
            :src="templateHeaderMedia.url"
            alt=""
            class="w-full max-h-64 object-cover"
          />
          <video
            v-else-if="templateHeaderMedia.type === 'video'"
            controls
            playsinline
            class="w-full"
          >
            <source :src="templateHeaderMedia.url" />
          </video>
          <div
            v-else
            class="flex items-center justify-between gap-3 bg-n-alpha-3 px-3 py-2"
          >
            <span class="truncate text-sm text-n-slate-12">
              {{ templateHeaderMedia.name || templateHeaderMedia.url }}
            </span>
            <a
              :href="templateHeaderMedia.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-n-brand-12"
            >
              {{ $t('CONVERSATION.DOWNLOAD') }}
            </a>
          </div>
        </div>
        <AttachmentChips :attachments="attachments" class="gap-2" />
        <ExternalAdReplyCard v-if="externalAdReply" :ad="externalAdReply" />
        <div v-if="renderContent" class="flex flex-col gap-1">
          <p
            v-if="externalAdReply"
            class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
          >
            {{ $t('CONVERSATION.EXTERNAL_AD.CUSTOMER_MESSAGE_LABEL') }}
          </p>
          <FormattedContent
            :key="`${id}-${rerenderKey}`"
            :content="renderContent"
          />
        </div>
        <template v-if="isTemplate">
          <div
            v-if="contentAttributes.submittedEmail"
            class="px-2 py-1 rounded-lg bg-n-alpha-3"
          >
            {{ contentAttributes.submittedEmail }}
          </div>
        </template>
        <TranslationToggle
          v-if="hasTranslations"
          class="-mt-3"
          :showing-original="renderOriginal"
          @toggle="handleSeeOriginal"
        />
      </div>
    </BaseBubble>
    <div
      v-if="hasTemplateButtons"
      class="rounded-xl px-4 py-2 flex flex-wrap gap-2 max-w-lg"
      :class="[buttonBubbleSurfaceClass, buttonBubbleAlignmentClass]"
    >
      <div
        v-for="button in templateButtons"
        :key="button.id"
        class="flex items-center justify-center flex-1 min-w-[7rem] px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-150"
        :class="buttonItemClass"
      >
        <span class="truncate">{{ button.label }}</span>
      </div>
    </div>
  </div>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
